import { decode } from './polyline.util';

export interface KmlTurnByTurnInstruction {
    action: string;
    instruction: string;
    lat: number;
    lng: number;
    direction?: string;
    nextRoadName?: string;
}

/**
 * Injects turn-by-turn instruction placemarks into an existing KML string.
 * Adds a new Folder with instruction Placemarks before the closing </Document> tag.
 */
export function injectTurnByTurnIntoKml(originalKml: string, instructions: KmlTurnByTurnInstruction[]): string {
    const style = `
  <Style id="tbtStyle">
    <IconStyle>
      <scale>0.8</scale>
      <Icon>
        <href>http://maps.google.com/mapfiles/kml/shapes/arrow.png</href>
      </Icon>
    </IconStyle>
  </Style>`;

    const placemarks = instructions
        .map(
            (instr, idx) => `
    <Placemark>
      <name>Paso ${idx + 1}: ${escapeXml(instr.action)}</name>
      <description>${escapeXml(instr.instruction)}${
                instr.nextRoadName ? ` — ${escapeXml(instr.nextRoadName)}` : ''
            }</description>
      <styleUrl>#tbtStyle</styleUrl>
      <Point>
        <coordinates>${instr.lng},${instr.lat},0</coordinates>
      </Point>
      <ExtendedData>
        <Data name="action"><value>${escapeXml(instr.action)}</value></Data>
        <Data name="stepIndex"><value>${idx + 1}</value></Data>${
                instr.direction
                    ? `\n        <Data name="direction"><value>${escapeXml(instr.direction)}</value></Data>`
                    : ''
            }
      </ExtendedData>
    </Placemark>`,
        )
        .join('\n');

    const tbtFolder = `
  <Folder>
    <name>Instrucciones de manejo</name>
${placemarks}
  </Folder>`;

    const closeDocTag = '</Document>';
    const insertIdx = originalKml.lastIndexOf(closeDocTag);

    if (insertIdx === -1) {
        // Malformed KML — wrap in a minimal Document
        return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
${style}
${tbtFolder}
  </Document>
</kml>`;
    }

    return (
        originalKml.slice(0, insertIdx) +
        style +
        tbtFolder +
        '\n' +
        closeDocTag +
        originalKml.slice(insertIdx + closeDocTag.length)
    );
}

/**
 * Builds a KML document from scratch with a route LineString and turn-by-turn placemarks.
 * Use injectTurnByTurnIntoKml when an existing KML is available.
 */
export function buildRouteKml(
    routeName: string,
    encodedPolylines: string[],
    instructions: KmlTurnByTurnInstruction[],
): string {
    const allCoords: Array<[number, number]> = [];

    for (const encoded of encodedPolylines) {
        const decoded = decode(encoded);
        for (const point of decoded.polyline) {
            allCoords.push([point[0], point[1]]);
        }
    }

    const lineCoords = allCoords.map(([lat, lng]) => `${lng},${lat},0`).join('\n');

    const instructionPlacemarks = instructions
        .map(
            (instr, idx) => `
    <Placemark>
      <name>Paso ${idx + 1}: ${escapeXml(instr.action)}</name>
      <description>${escapeXml(instr.instruction)}${
                instr.nextRoadName ? ` — ${escapeXml(instr.nextRoadName)}` : ''
            }</description>
      <styleUrl>#tbtStyle</styleUrl>
      <Point>
        <coordinates>${instr.lng},${instr.lat},0</coordinates>
      </Point>
      <ExtendedData>
        <Data name="action"><value>${escapeXml(instr.action)}</value></Data>
        <Data name="stepIndex"><value>${idx + 1}</value></Data>${
                instr.direction
                    ? `\n        <Data name="direction"><value>${escapeXml(instr.direction)}</value></Data>`
                    : ''
            }
      </ExtendedData>
    </Placemark>`,
        )
        .join('\n');

    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(routeName)}</name>
    <description>Ruta con instrucciones de manejo</description>
    <Style id="routeLineStyle">
      <LineStyle>
        <color>ff0000ff</color>
        <width>4</width>
      </LineStyle>
    </Style>
    <Style id="tbtStyle">
      <IconStyle>
        <scale>0.8</scale>
        <Icon>
          <href>http://maps.google.com/mapfiles/kml/shapes/arrow.png</href>
        </Icon>
      </IconStyle>
    </Style>
    <Folder>
      <name>Recorrido</name>
      <Placemark>
        <name>${escapeXml(routeName)}</name>
        <styleUrl>#routeLineStyle</styleUrl>
        <LineString>
          <tessellate>1</tessellate>
          <coordinates>
${lineCoords}
          </coordinates>
        </LineString>
      </Placemark>
    </Folder>
    <Folder>
      <name>Instrucciones de manejo</name>
${instructionPlacemarks}
    </Folder>
  </Document>
</kml>`;
}

function escapeXml(str: unknown): string {
    if (str === null || str === undefined) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
